import { motion } from "framer-motion";
import { Target, Sparkles, Cpu, ShieldCheck, Users, TrendingUp } from "lucide-react";
import { WHY_SPRINGXE, IMAGES } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";

const iconMap = {
    target: Target,
    sparkles: Sparkles,
    cpu: Cpu,
    shield: ShieldCheck,
    users: Users,
    "trending-up": TrendingUp,
};

const WhySpringXE = () => {
    return (
        <section data-testid="why-section" className="sxe-section relative overflow-hidden">
            <BgLayer variant="orbs" />
            <div className="sxe-container relative">
                <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 items-end">
                    <div className="max-w-3xl">
                        <div className="overline"><span className="dot" /> Why SpringXE</div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white mt-5">
                            A capability engine.{" "}
                            <span className="text-gradient">Not another LMS.</span>
                        </h2>
                        <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6 max-w-2xl">
                            SpringXE is designed for the way modern enterprises build capability — anchored on skills, powered by AI, and accountable to outcomes that leadership actually cares about.
                        </p>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/25 hidden lg:block"
                    >
                        <img src={IMAGES.dashboardPreview} alt="SpringXE learner dashboard" className="w-full h-[280px] object-cover object-top" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-transparent to-transparent" />
                    </motion.div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
                    {WHY_SPRINGXE.map((w, i) => {
                        const Icon = iconMap[w.icon] || Sparkles;
                        return (
                            <motion.div
                                key={w.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.05 }}
                                className="sxe-card p-8 relative overflow-hidden group"
                                data-testid={`why-card-${i}`}
                            >
                                {/* Card top gradient accent */}
                                <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#C266FF]/60 to-transparent opacity-40 group-hover:opacity-100 transition-opacity" />
                                {/* Card index badge */}
                                <div className="absolute top-6 right-6 font-mono text-[10px] tracking-widest text-[#75668C] group-hover:text-[#C266FF] transition-colors">
                                    0{i + 1}
                                </div>

                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(161,0,255,0.6)] group-hover:scale-105 transition-transform">
                                    <Icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="font-display text-white text-2xl font-medium tracking-tight mt-6">
                                    {w.title}
                                </h3>
                                <p className="text-[#B0A3C4] text-[14.5px] leading-relaxed mt-3">{w.desc}</p>

                                {/* Bottom hover indicator */}
                                <div className="mt-6 flex items-center gap-2 text-[#E9CCFF] text-xs font-display uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="w-6 h-px bg-[#C266FF]" /> Learn more
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default WhySpringXE;
